import { z } from "zod";
import type {
    IsoAndUnixTimestamp} from "../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
} from "../date-time/iso-and-unix";
import type { ProviderId} from "../providers/provider-id";
import { providerIdZodSchema } from "../providers/provider-id";
import type {
    HexColorTheme} from "../stylable/hex-color/hex-color-theme";
import {
    hexColorThemeFactory,
    hexColorThemeZodSchema,
} from "../stylable/hex-color/hex-color-theme";
import type {
    Translatable} from "../translations/translation";
import {
    translatableZodSchema,
} from "../translations/translation";
import type { Branding } from "./branding";
import type {
    BrandingBackground} from "./branding-background";
import {
    brandingBackgroundZodSchema,
} from "./branding-background";
import type { BrandingId} from "./branding-id";
import { brandingIdZodSchema } from "./branding-id";

type _BrandingDto = {
    _id: BrandingId;
    _createdAt: IsoAndUnixTimestamp;
    _type: "BRANDING_DTO";
    state: "DRAFT" | "PUBLISHED";
    name: Translatable;
    providerId: ProviderId;
    colorThemes: {
        darkColorTheme?: never;
        lightColorTheme: HexColorTheme;
    };
    /**
     * Overrides back links and continuation buttons to always \
     * send the user to the provider's selected storefront url. \
     * @type {string}
     */
    storefrontUrl?: string;

    background?: BrandingBackground;

    product?: {
        background: BrandingBackground;
        peripheralBackground: BrandingBackground;
        margin: [large: number, medium: number, small: number];
    };
    receipt?: {
        background: BrandingBackground;
        peripheralBackground: BrandingBackground;
    };
};
const brandingDtoZodSchema = z.object({
    _id: brandingIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("BRANDING_DTO"),
    name: translatableZodSchema,
    state: z.enum(["DRAFT", "PUBLISHED"]),
    providerId: providerIdZodSchema,
    colorThemes: z.object({
        darkColorTheme: z.never().optional(),
        lightColorTheme: hexColorThemeZodSchema,
    }),
    storefrontUrl: z.string().url().optional(),

    background: brandingBackgroundZodSchema.optional(),

    product: z
        .object({
            background: brandingBackgroundZodSchema,
            peripheralBackground: brandingBackgroundZodSchema,
            margin: z.tuple([z.number(), z.number(), z.number()]),
        })
        .nullable(),
    receipt: z
        .object({
            background: brandingBackgroundZodSchema,
            peripheralBackground: brandingBackgroundZodSchema,
        })
        .nullable(),
});

type BrandingDto = z.infer<typeof brandingDtoZodSchema>;

function brandingDtoFactory(
    dto: Branding,
    providerId?: ProviderId,
    state: "DRAFT" | "PUBLISHED" = "DRAFT",
): BrandingDto {
    const { colorThemes, _type, ...data } = dto;
    const _providerId = providerId || dto.providerId;
    if (!_providerId)
        throw new Error(
            "Branding needs and owner id to be converted into a branding dto object; " +
                dto._id,
        );

    const input = {
        ...data,
        providerId: _providerId,
        _type: "BRANDING_DTO",
        state: state,
        colorThemes: {
            lightColorTheme: hexColorThemeFactory(colorThemes.light),
        },
    };

    const branding: BrandingDto = brandingDtoZodSchema.parse(input);
    return branding;
}

export { brandingDtoFactory, brandingDtoZodSchema };
export type { BrandingDto };
